import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ReactComponent as CopyDownIcon } from '../../assets/icons/copy-down.svg';
import TableCell from './TableCell';
import TableHeader from './TableHeader';
import {
  MutableDataTableProps,
  EditableTableData,
  VisibleRange,
  Viewport,
  ScrollPosition,
  CellPosition,
  HeaderPosition,
  FocusedCell,
} from './types';
import { CustomTooltip } from '@components';
import { CopyAll, DynamicFeed } from '@mui/icons-material';

const COLUMN_WIDTH_DEFAULT = 140;
const ROW_HEIGHT = 40;
const BUFFER_SIZE = 2;
const COPY_ACTIONS_WIDTH = 50;

const getColumnWidths = (columns: MutableDataTableProps['columns'], containerWidth: number) => {
  const equalShare = containerWidth > 0 ? Math.floor(containerWidth / columns.length) : 0;
  return columns.map((col) => Math.max(col.minWidth ?? COLUMN_WIDTH_DEFAULT, equalShare));
};

const getColumnOffsets = (widths: number[]) => {
  let offset = 0;
  return widths.map((width) => {
    const current = offset;
    offset += width;
    return current;
  });
};

const findScrollableColumnIndexAtX = (
  x: number,
  fixedCount: number,
  widths: number[],
  offsets: number[],
) => {
  for (let i = fixedCount; i < widths.length; i++) {
    if (offsets[i] + widths[i] > x) {
      return i - fixedCount;
    }
  }
  return Math.max(0, widths.length - fixedCount - 1);
};

const MutableDataTable = ({
  columns,
  data,
  setData,
  reRenderCounter,
  handlePaste,
  onCellClick,
  onMouseDown,
  onMouseOver,
  onMouseUp,
  scrollToBottom = false,
  autofocus = false,
  showSeparation = false,
  onCopyAction,
  disabled = false,
  copyIconVariant = 'default',
}: MutableDataTableProps) => {
  const [shouldUpdateRenderId, setShouldUpdateRenderId] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({ x: 0, y: 0 });
  const [viewport, setViewport] = useState<Viewport>({
    width: 0,
    height: 0,
  });
  const [focusedCell, setFocusedCell] = useState<FocusedCell | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Find the first editable cell for autofocus
  const firstEditableCell = useMemo(() => {
    if (!autofocus) return null;
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < columns.length; j++) {
        if (data[i] && data[i][j] && data[i][j].isEditable && !data[i][j].isDisabled) {
          return { row: i, col: j };
        }
      }
    }
    return null;
  }, [autofocus, data, columns]);

  // TODO: Deprecte the following function
  const updateRenderId = useCallback(() => {
    // setRenderId((prev) => (prev + 1) % 1000);
  }, []);

  // Get fixed and scrollable columns from props
  const fixedColumns = useMemo(() => columns.filter((col) => col.isFixed), [columns]);
  const scrollableColumns = useMemo(() => columns.filter((col) => !col.isFixed), [columns]);

  const columnWidths = useMemo(
    () => getColumnWidths(columns, viewport.width),
    [columns, viewport.width],
  );
  const columnOffsets = useMemo(() => getColumnOffsets(columnWidths), [columnWidths]);
  const fixedColumnsWidth = useMemo(
    () => columnWidths.slice(0, fixedColumns.length).reduce((sum, width) => sum + width, 0),
    [columnWidths, fixedColumns.length],
  );

  // Calculate total dimensions
  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
  const totalHeight = (data.length + 1) * ROW_HEIGHT;

  useEffect(() => {
    setShouldUpdateRenderId(true);
  }, [reRenderCounter]);

  // Handle viewport size detection
  useEffect(() => {
    const updateViewportSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setViewport({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateViewportSize();

    const resizeObserver = new ResizeObserver(updateViewportSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [columns]);

  // Handle scroll to bottom functionality - run once on mount
  useEffect(() => {
    if (scrollToBottom && scrollableRef.current) {
      scrollableRef.current.scrollTop = scrollableRef.current.scrollHeight;
    }
  }, [scrollableRef.current]);

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollPosition({
      x: target.scrollLeft,
      y: target.scrollTop,
    });
  }, []);

  // Calculate visible range with buffer
  const visibleRange = useMemo((): VisibleRange => {
    const startCol = Math.max(
      0,
      findScrollableColumnIndexAtX(
        scrollPosition.x,
        fixedColumns.length,
        columnWidths,
        columnOffsets,
      ) - BUFFER_SIZE,
    );
    const endCol = Math.min(
      scrollableColumns.length,
      findScrollableColumnIndexAtX(
        scrollPosition.x + viewport.width,
        fixedColumns.length,
        columnWidths,
        columnOffsets,
      ) +
        BUFFER_SIZE +
        1,
    );

    const startRow = Math.max(0, Math.floor(scrollPosition.y / ROW_HEIGHT) - BUFFER_SIZE);
    const endRow = Math.min(
      data.length,
      Math.ceil((scrollPosition.y + viewport.height) / ROW_HEIGHT) + BUFFER_SIZE,
    );

    return { startCol, endCol, startRow, endRow };
  }, [
    scrollPosition,
    viewport,
    data.length,
    scrollableColumns.length,
    fixedColumns.length,
    columnWidths,
    columnOffsets,
  ]);

  // Helper function to check if a cell is at the edge of a selection
  const isCellAtSelectionEdge = useCallback(
    (
      row: number,
      col: number,
    ): {
      isTopEdge: boolean;
      isBottomEdge: boolean;
      isLeftEdge: boolean;
      isRightEdge: boolean;
    } => {
      const isSelected = data[row]?.[col]?.isSelected ?? false;
      if (!isSelected) {
        return { isTopEdge: false, isBottomEdge: false, isLeftEdge: false, isRightEdge: false };
      }

      // Check top edge: first row or cell above is not selected
      const isTopEdge = row === 0 || !(data[row - 1]?.[col]?.isSelected ?? false);

      // Check bottom edge: last row or cell below is not selected
      const isBottomEdge = row === data.length - 1 || !(data[row + 1]?.[col]?.isSelected ?? false);

      // Check left edge: first column or cell to the left is not selected
      const isLeftEdge = col === 0 || !(data[row]?.[col - 1]?.isSelected ?? false);

      // Check right edge: last column or cell to the right is not selected
      const isRightEdge =
        col === columns.length - 1 || !(data[row]?.[col + 1]?.isSelected ?? false);

      return { isTopEdge, isBottomEdge, isLeftEdge, isRightEdge };
    },
    [data, columns],
  );

  const fixedHeaders = useMemo((): HeaderPosition[] => {
    return fixedColumns.map((col, index) => ({
      col: index,
      value: col.label,
      x: columnOffsets[index],
      y: 0,
    }));
  }, [fixedColumns, columnOffsets]);

  const fixedCells = useMemo((): CellPosition[] => {
    const cells: CellPosition[] = [];
    for (let i = visibleRange.startRow; i < visibleRange.endRow; i++) {
      for (let j = 0; j < fixedColumns.length; j++) {
        if (data[i] && data[i][j] !== undefined) {
          const selectionEdges = isCellAtSelectionEdge(i, j);
          cells.push({
            row: i,
            col: j,
            value: data[i][j].value,
            x: columnOffsets[j],
            y: i * ROW_HEIGHT,
            isEditable: data[i][j].isEditable,
            isDisabled: data[i][j].isDisabled,
            isError: data[i][j].isError,
            isSelected: data[i][j].isSelected,
            isCopied: data[i][j].isCopied,
            isPasted: data[i][j].isPasted,
            format: data[i][j].format,
            variant: data[i][j].variant || 'none',
            isSelectionTopEdge: selectionEdges.isTopEdge,
            isSelectionBottomEdge: selectionEdges.isBottomEdge,
            isSelectionLeftEdge: selectionEdges.isLeftEdge,
            isSelectionRightEdge: selectionEdges.isRightEdge,
          });
        }
      }
    }

    if (shouldUpdateRenderId) {
      updateRenderId();
      setShouldUpdateRenderId(false);
    }

    return cells;
  }, [visibleRange, data, fixedColumns, columnOffsets, isCellAtSelectionEdge]);

  const visibleHeaders = useMemo((): HeaderPosition[] => {
    const headers: HeaderPosition[] = [];
    for (let i = visibleRange.startCol; i < visibleRange.endCol; i++) {
      const columnIndex = fixedColumns.length + i;
      if (columnIndex < columns.length) {
        headers.push({
          col: columnIndex,
          value: columns[columnIndex].label,
          x: columnOffsets[columnIndex],
          y: 0,
        });
      }
    }

    return headers;
  }, [visibleRange, columns, fixedColumns.length, columnOffsets]);

  // Generate visible cells
  const visibleCells = useMemo((): CellPosition[] => {
    const cells: CellPosition[] = [];

    for (let i = visibleRange.startRow; i < visibleRange.endRow; i++) {
      for (let j = visibleRange.startCol; j < visibleRange.endCol; j++) {
        const columnIndex = fixedColumns.length + j;
        if (data[i] && data[i][columnIndex] !== undefined && columnIndex < columns.length) {
          const selectionEdges = isCellAtSelectionEdge(i, columnIndex);
          cells.push({
            row: i,
            col: columnIndex,
            value: data[i][columnIndex].value,
            x: columnOffsets[columnIndex],
            y: (i + 1) * ROW_HEIGHT,
            isEditable: disabled ? false : data[i][columnIndex].isEditable,
            isDisabled: data[i][columnIndex].isDisabled,
            isError: data[i][columnIndex].isError,
            isSelected: data[i][columnIndex].isSelected,
            isCopied: data[i][columnIndex].isCopied,
            isPasted: data[i][columnIndex].isPasted,
            format: data[i][columnIndex].format,
            variant: data[i][columnIndex].variant || 'none',
            isSelectionTopEdge: selectionEdges.isTopEdge,
            isSelectionBottomEdge: selectionEdges.isBottomEdge,
            isSelectionLeftEdge: selectionEdges.isLeftEdge,
            isSelectionRightEdge: selectionEdges.isRightEdge,
          });
        }
      }
    }

    if (shouldUpdateRenderId) {
      updateRenderId();
      setShouldUpdateRenderId(false);
    }

    return cells;
  }, [
    visibleRange,
    data,
    columns.length,
    fixedColumns.length,
    columnOffsets,
    isCellAtSelectionEdge,
  ]);

  const handleCellChange = (value: number | string | null, row: number, col: number) => {
    setData((prevData: EditableTableData) => {
      // Add bounds checking to prevent undefined access
      if (!prevData || !Array.isArray(prevData)) {
        console.error('Invalid prevData structure in handleCellChange');
        return prevData;
      }

      if (row < 0 || row >= prevData.length) {
        console.error(`Row index ${row} out of bounds. Data length: ${prevData.length}`);
        return prevData;
      }

      if (!Array.isArray(prevData[row])) {
        console.error(`Row ${row} is not an array:`, prevData[row]);
        return prevData;
      }

      if (col < 0 || col >= prevData[row].length) {
        console.error(
          `Column index ${col} out of bounds for row ${row}. Row length: ${prevData[row].length}`,
        );
        return prevData;
      }

      const newData = JSON.parse(JSON.stringify(prevData));
      // const newData = [...prevData];
      // newData[row] = [...newData[row]]; // Create a copy of the row
      newData[row][col] = { ...newData[row][col], value };
      return newData;
    });
  };

  const handleCellFocus = useCallback((row: number, col: number) => {
    setFocusedCell({ row, col });
  }, []);

  const handleCellBlur = useCallback(() => {
    setFocusedCell(null);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
        }}
      >
        {/* Table Container */}
        <Box
          id="mutable-data-table-container"
          ref={containerRef}
          sx={{
            width: onCopyAction ? `calc(100% - ${COPY_ACTIONS_WIDTH}px)` : '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid #737F86',
            borderRadius: '8px',
            boxSizing: 'border-box',
            userSelect: 'none',
          }}
        >
          {/* Scrollable container */}
          <Box
            id="mutable-data-table-scrollable"
            ref={scrollableRef}
            onScroll={handleScroll}
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {/* Virtual content area - this creates the scrollbars */}
            <Box
              id="mutable-data-table-virtual-content"
              sx={{
                width: totalWidth,
                height: totalHeight,
                position: 'relative',
              }}
            >
              {/* Rendered headers */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  width: totalWidth,
                  height: ROW_HEIGHT,
                  zIndex: 1000,
                  backgroundColor: '#2F736E',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    width: fixedColumnsWidth,
                    height: ROW_HEIGHT,
                    zIndex: 1001,
                    backgroundColor: '#2F736E',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  }}
                >
                  {fixedHeaders.map(({ col, value, x, y }) => (
                    <TableHeader
                      key={col}
                      col={col}
                      value={value}
                      x={x}
                      y={y}
                      width={columnWidths[col]}
                      height={ROW_HEIGHT}
                      isFirstColumn={col === 0}
                    />
                  ))}
                </Box>

                {visibleHeaders.map(({ col, value, x, y }) => (
                  <TableHeader
                    key={col}
                    col={col}
                    value={value}
                    x={x}
                    y={y}
                    width={columnWidths[col]}
                    height={ROW_HEIGHT}
                  />
                ))}
              </Box>

              {/* Rendered cells */}
              {/* Fixed cells */}
              <Box
                sx={{
                  position: 'sticky',
                  left: 0,
                  width: fixedColumnsWidth,
                  height: totalHeight - ROW_HEIGHT,
                  backgroundColor: 'white',
                  zIndex: 100,
                }}
              >
                {fixedCells.map(
                  ({
                    row,
                    col,
                    value,
                    x,
                    y,
                    isEditable,
                    isDisabled,
                    isError,
                    isSelected,
                    isCopied,
                    isPasted,
                    format,
                    variant,
                    isSelectionTopEdge,
                    isSelectionBottomEdge,
                    isSelectionLeftEdge,
                    isSelectionRightEdge,
                  }) => (
                    <TableCell
                      key={`${row}-${col}`}
                      value={value}
                      isEditable={isEditable}
                      isDisabled={isDisabled}
                      variant={variant}
                      row={row}
                      col={col}
                      format={format}
                      x={x}
                      y={y}
                      width={columnWidths[col]}
                      height={ROW_HEIGHT}
                      onChange={handleCellChange}
                      onPaste={handlePaste}
                      onFocus={handleCellFocus}
                      onBlur={handleCellBlur}
                      onClick={onCellClick}
                      onMouseDown={onMouseDown}
                      onMouseOver={onMouseOver}
                      onMouseUp={onMouseUp}
                      isFocused={focusedCell?.row === row && focusedCell?.col === col}
                      sx={{ backgroundColor: '#2F736E1F' }}
                      isLeftEdge={col === 0}
                      isRightEdge={col === columns.length - 1}
                      isBottomEdge={row === data.length - 1}
                      isError={isError}
                      isSelected={isSelected}
                      isCopied={isCopied}
                      isPasted={isPasted}
                      isSelectionTopEdge={isSelectionTopEdge}
                      isSelectionBottomEdge={isSelectionBottomEdge}
                      isSelectionLeftEdge={isSelectionLeftEdge}
                      isSelectionRightEdge={isSelectionRightEdge}
                      autofocus={
                        autofocus &&
                        firstEditableCell?.row === row &&
                        firstEditableCell?.col === col
                      }
                    />
                  ),
                )}
              </Box>

              {/* Scrollable cells */}
              {visibleCells.map(
                ({
                  row,
                  col,
                  value,
                  x,
                  y,
                  isEditable,
                  isDisabled,
                  isError,
                  isSelected,
                  isCopied,
                  isPasted,
                  format,
                  variant,
                  isSelectionTopEdge,
                  isSelectionBottomEdge,
                  isSelectionLeftEdge,
                  isSelectionRightEdge,
                }) => (
                  <TableCell
                    key={`${row}-${col}`}
                    value={value}
                    isEditable={isEditable}
                    isDisabled={isDisabled}
                    variant={variant}
                    row={row}
                    col={col}
                    format={format}
                    x={x}
                    y={y}
                    width={columnWidths[col]}
                    height={ROW_HEIGHT}
                    onChange={handleCellChange}
                    onPaste={handlePaste}
                    onFocus={handleCellFocus}
                    onBlur={handleCellBlur}
                    onClick={onCellClick}
                    onMouseDown={onMouseDown}
                    onMouseOver={onMouseOver}
                    onMouseUp={onMouseUp}
                    isFocused={focusedCell?.row === row && focusedCell?.col === col}
                    isLeftEdge={col === 0}
                    isRightEdge={col === columns.length - 1}
                    isBottomEdge={row === data.length - 1}
                    isError={isError}
                    isSelected={isSelected}
                    isCopied={isCopied}
                    isPasted={isPasted}
                    isSelectionTopEdge={isSelectionTopEdge}
                    isSelectionBottomEdge={isSelectionBottomEdge}
                    isSelectionLeftEdge={isSelectionLeftEdge}
                    isSelectionRightEdge={isSelectionRightEdge}
                  />
                ),
              )}
            </Box>
          </Box>
        </Box>

        {/* Copy Actions Column - Outside Table */}
        {onCopyAction && (
          <Box
            sx={{
              width: `${COPY_ACTIONS_WIDTH}px`,
              flexShrink: 0,
              marginLeft: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header spacer */}
            <Box
              sx={{
                height: ROW_HEIGHT,
              }}
            />
            {/* Action buttons for each row */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {data.map((_, rowIndex) => (
                <Box
                  key={rowIndex}
                  sx={{
                    height: ROW_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    onClick={() => onCopyAction?.(rowIndex, 'ALL')}
                    sx={{
                      padding: '4px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(47, 115, 110, 0.08)',
                      },
                    }}
                  >
                    <CustomTooltip
                      placement="top"
                      customStyle={{
                        '& .MuiTooltip-tooltip': {
                          color: '#0D0D0D',
                          borderRadius: '0.5rem',
                          bgcolor: '#AECBD0',
                          backdropFilter: 'blur(5px)',
                          boxShadow: 2,
                          paddingX: '0.75rem',
                          paddingY: '0.5rem',
                        },
                      }}
                      content={
                        <div
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 550,
                            fontSize: '0.8rem',
                          }}
                        >
                          Copy to all rows
                        </div>
                      }
                    >
                      {copyIconVariant === 'default' ? (
                        <ContentCopyIcon sx={{ fontSize: 16, color: '#2F736E' }} />
                      ) : (
                        <CopyAll sx={{ fontSize: 16, color: '#2F736E' }} />
                      )}
                    </CustomTooltip>
                  </Box>
                  <Box
                    onClick={() => onCopyAction?.(rowIndex, 'SUBSEQUENT')}
                    sx={{
                      padding: '4px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(47, 115, 110, 0.08)',
                      },
                    }}
                  >
                    <CustomTooltip
                      placement="top"
                      customStyle={{
                        '& .MuiTooltip-tooltip': {
                          color: '#0D0D0D',
                          borderRadius: '0.5rem',
                          bgcolor: '#AECBD0',
                          backdropFilter: 'blur(5px)',
                          boxShadow: 2,
                          paddingX: '0.75rem',
                          paddingY: '0.5rem',
                        },
                      }}
                      content={
                        <div
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 550,
                            fontSize: '0.8rem',
                          }}
                        >
                          Copy to subsequent rows
                        </div>
                      }
                    >
                      {copyIconVariant === 'default' ? (
                        <CopyDownIcon style={{ width: 16, height: 16 }} />
                      ) : (
                        <DynamicFeed sx={{ fontSize: 16, color: '#2F736E' }} />
                      )}
                    </CustomTooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MutableDataTable;
